/**
 * SOURCES. Where households came from, and what each channel cost to get them. Priority 7 of the
 * business brief, verbatim: "Say where clients came from. Each client carries a source. Without it
 * the business cannot tell which of its channels is worth money, which is the single most
 * expensive thing to guess at." `docs/plan-crm-attribution.md` section 7 report A, state 2.
 *
 * IT IS A COUNT OF WHAT WAS RECORDED, AND IT SAYS SO. Every number here but one is rows this
 * system holds inside a window of two days, and the caption under the table says that out loud,
 * because a report read as the truth about the world rather than as the truth about the records is
 * how a channel gets killed for a month somebody forgot to type in. Discarded rows are excluded on
 * the server; nothing is back-dated anywhere.
 *
 * The one that is not windowed is reviews given, and it carries its own words wherever it is
 * printed: statement 7 of `api/src/reports/sources.ts` counts every household on the channel whose
 * review link this system holds, because a link has no date to window it by. So the cell reads
 * "given to date" rather than "given", beside a "0 asked" that IS the eight days on the filter
 * bar, and the caption names the exception. `reviewsGivenWords` in `web/src/reports.ts` is the one
 * place those words are written.
 *
 * TWO REPORTS, AND ONLY THE FIRST OF THEM MAY BE CORAL. Report B, the rules with a threshold,
 * stands at the top: state 3 of section 8, `GET /api/reports/kill-rules`, six rules as a number, a
 * threshold and a day. A rule near its threshold is a gap somebody has to close before it passes,
 * so it wears the colour. Nothing in the channel table below it does, and that is the same rule
 * rather than a different one: a channel that produced nothing may be a channel to stop paying
 * for, and that is a decision an owner makes with the plan open, not an alarm an interface raises.
 * The planning figure and twice the top of it are printed there beside the real figure and nothing
 * in that table compares them. `ruleIsAGap` in `web/src/reports.ts` is the single expression that
 * paints anything on this screen coral, and it reads a boolean the server sent: two leads, twenty
 * dollars and seven days live in `api/src/reports/kill-rules.ts` and are not repeated here.
 *
 * THE TWO HALVES ANSWER OVER DIFFERENT SPANS, and the captions say so. The channel table is the
 * window on the filter bar. The rules are counted from the first lead or the first dollar to
 * today, whatever that filter says, because a threshold counted over a chosen period is a
 * threshold that moves when somebody presses a select.
 *
 * AND THE COST COLUMN IS TYPED IN ON THE SPEND SCREEN, one press from the header here, which is
 * where somebody looking at a dash in that column is standing when they realise why it is a dash.
 *
 * A DASH IS NOT A ZERO. `web/src/reports.ts` holds that rule and every other piece of arithmetic
 * on this screen, with its own tests: an empty expenses table says nothing at all about what a
 * channel cost, and $0.00 in that cell would be this screen inventing the number the whole report
 * exists to observe. The sources the plan budgets nothing for say `free` instead.
 *
 * TWO CALLS, NEVER A THIRD. The channel-level report is eight statements whatever the vocabulary
 * grows to, and the roll-up for one channel is fetched only when somebody opens that channel. The
 * free plan allows fifty subrequests per invocation and every D1 call is one of them.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HELD_BACK_LABELS, type Channel } from '@fk/shared';
import {
  ApiError,
  getChannelReport,
  getKillRules,
  getSourcesReport,
  type ChannelReport,
  type ChannelReportRow,
  type KillRuleRow,
  type KillRulesReport,
  type Me,
  type PlanningRange,
  type SourcesReport,
} from '../api.ts';
import { Card, Detail, MEASURE, ScreenTitle } from '../components/AppShell.tsx';
import {
  DataTable,
  FilterBar,
  FilterSelect,
  PanelRule,
  SidePanel,
  SortableHeader,
  SplitLayout,
  useBesideView,
  useWideView,
  useWiderView,
  type DataColumn,
  type SortDirection,
} from '../components/ListView.tsx';
import { Busy, ErrorNotice } from '../components/Notice.tsx';
import { GapPill } from '../components/StatusPills.tsx';
import { formatDay, money } from '../format.ts';
import {
  FIRST_ORDER,
  FIRST_PERIOD,
  PERIODS,
  PERIOD_LABELS,
  channelWords,
  costEachWords,
  costWords,
  countWords,
  counterLines,
  customWindow,
  detailWords,
  everyColumnFits,
  firesOnNote,
  firesOnWords,
  leadToBookedWords,
  periodWindow,
  planningRangeWords,
  remainingWords,
  reviewsGivenWords,
  reviewsWords,
  rollUpBesideTheTable,
  rollUpUnderTheCard,
  ruleChannelWords,
  ruleIsAGap,
  ruleNotes,
  sortChannelRows,
  sumDetails,
  twiceTheTopWords,
  whyNothingIsLeft,
  type DayWindow,
  type Ordering,
  type Period,
  type PlanningCents,
  type SortKey,
} from '../reports.ts';

export function Sources({ me }: { me: Me }) {
  const [period, setPeriod] = useState<Period>(FIRST_PERIOD);
  const [typed, setTyped] = useState<DayWindow>(() => periodWindow(FIRST_PERIOD, me.today));
  const [report, setReport] = useState<SourcesReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [by, setBy] = useState<Ordering>(FIRST_ORDER);
  const [opened, setOpened] = useState<Channel | null>(null);
  /**
   * Two questions, not one. `wide` decides WHERE the roll-up goes: the split's panel from md up,
   * beside the table from xl and under it below xl (ops #56), and under the card somebody pressed
   * below md, because a panel that renders after ten cards is
   * roughly 2,700px below the finger that asked for it and reads as a screen that did nothing.
   * `wider` decides how many columns the table carries, and `everyColumnFits` takes the open panel
   * off that width where `besideView` says it stands beside the table, from xl; below xl it stands
   * under the table and takes nothing off it.
   */
  const wide = useWideView();
  const wider = useWiderView();
  const besideView = useBesideView();

  /**
   * The window, worked out from the server's own day and never from the phone's clock. A custom
   * window that is half typed or runs backwards resolves to nothing, and nothing is asked for.
   */
  const chosen =
    period === 'custom' ? customWindow(typed.from, typed.to) : periodWindow(period, me.today);
  const from = chosen === null ? null : chosen.from;
  const to = chosen === null ? null : chosen.to;

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (from === null || to === null) return;
    let live = true;
    setError(null);
    setReport(null);
    getSourcesReport(from, to)
      .then((given) => {
        if (live) setReport(given);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The report did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [from, to, attempt]);

  const choosePeriod = (next: Period | '') => {
    if (next === '') return;
    // The two boxes open on whatever window was on the screen, so choosing the custom period is
    // a starting point to edit rather than an empty form and a blank report.
    if (next === 'custom' && chosen !== null) setTyped(chosen);
    setPeriod(next);
  };

  // A number opens on its largest, which is what somebody pressing "Leads" is looking for, and the
  // channel column opens on A to Z, which is what somebody pressing a column of words is.
  const turn = (key: SortKey) =>
    setBy((current) =>
      current.key === key
        ? { key, direction: current.direction === 'ascending' ? 'descending' : 'ascending' }
        : { key, direction: key === 'channel' ? 'ascending' : 'descending' },
    );
  const pointing = (key: SortKey): SortDirection => (by.key === key ? by.direction : 'none');

  /** The opened channel where the panel is the place for it, and nothing where the card is. */
  const beside = rollUpBesideTheTable(wide, opened !== null) ? opened : null;

  const planning = new Map<Channel, PlanningRange>(
    (report?.planning ?? []).map((figure) => [figure.channel, figure]),
  );
  const figureFor = (channel: Channel): PlanningRange | null => planning.get(channel) ?? null;
  const rows = report === null ? [] : sortChannelRows(report.channels, by);

  return (
    <div>
      <ScreenTitle
        title="Where households came from"
        subtitle="What each channel has produced, and what it cost."
      />

      {/*
       * The door to the other half of priority 7. Cost is money somebody typed in, and until
       * somebody does the whole column reads a dash, so the way to type it is on this screen and
       * not two rails away.
       */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-navy/70">
          Cost is the marketing spend typed in against a channel.
        </p>
        <Link
          to="/spend"
          className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
        >
          Spend
        </Link>
      </div>

      <TheRulesWithAThreshold />

      <FilterBar>
        <FilterSelect
          id="sources-period"
          label="Period"
          value={period}
          onChange={choosePeriod}
          options={PERIODS}
          toLabel={(name) => PERIOD_LABELS[name]}
          className="md:w-[180px]"
        />
        {period === 'custom' ? (
          <>
            <DayField
              id="sources-from"
              label="From"
              value={typed.from}
              onChange={(day) => setTyped((current) => ({ ...current, from: day }))}
            />
            <DayField
              id="sources-to"
              label="To"
              value={typed.to}
              onChange={(day) => setTyped((current) => ({ ...current, to: day }))}
            />
          </>
        ) : null}
      </FilterBar>

      {chosen === null ? (
        <p className="rounded-xl border border-dashed border-navy/25 p-5 text-center text-sm text-navy/70">
          <span className={`mx-auto block ${MEASURE}`}>
            Type two days, the earlier one first, and the report is drawn for those days and both
            ends of them.
          </span>
        </p>
      ) : error ? (
        <ErrorNotice title="The report did not load" message={error.message} onRetry={reload} />
      ) : report === null ? (
        <Busy what="the report" />
      ) : (
        <SplitLayout
          panel={
            beside === null ? undefined : (
              <ChannelPanel
                key={beside}
                channel={beside}
                row={report.channels.find((each) => each.channel === beside)}
                from={report.from}
                to={report.to}
                onClose={() => setOpened(null)}
              />
            )
          }
        >
          <p className={`mb-2 text-xs text-navy/60 ${MEASURE}`}>
            {formatDay(report.from)} to {formatDay(report.to)}. Press a channel for the words typed
            under it. A dash means nothing was recorded to count, or nothing to divide by.
          </p>

          <DataTable
            label="Channels"
            groups={[{ key: 'channels', rows }]}
            columns={
              everyColumnFits(wider, opened !== null && besideView)
                ? everyColumn(pointing, turn, figureFor, setOpened)
                : theFiveThatFit(pointing, turn, figureFor, setOpened)
            }
            rowKey={(row) => row.channel}
            rowClassName={(row) =>
              row.channel === opened
                ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                : ''
            }
            card={(row) => (
              <>
                <ChannelCard
                  row={row}
                  figure={figureFor(row.channel)}
                  opened={row.channel === opened}
                  onOpen={() => setOpened(row.channel)}
                />
                {/*
                 * On a phone the roll-up is the next thing under the card that was pressed, not
                 * the thing under all eleven of them. `SplitLayout` stacks its panel after the
                 * whole list below md, and the list is never shorter than the ten channels the
                 * plan funds, so a panel there answers a tap a screen and a half away from it.
                 * The condition is on `wide` rather than on CSS because the cards are rendered at
                 * every width and only hidden at md: without it a desk would fetch the channel
                 * twice, once for the panel and once for a card nobody can see.
                 */}
                {rollUpUnderTheCard(wide, row.channel === opened) ? (
                  <div className="mt-3">
                    <ChannelPanel
                      channel={row.channel}
                      row={row}
                      from={report.from}
                      to={report.to}
                      onClose={() => setOpened(null)}
                    />
                  </div>
                ) : null}
              </>
            )}
          />

          <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
            Every number here is a count of what this system recorded between those two days, and
            rows that have been discarded are left out of all of them. The one exception is reviews
            given, which is a running total to date: what is kept is a link, and a link carries no
            date to count it by.
          </p>
          <p className={`mt-1 text-xs text-navy/60 ${MEASURE}`}>
            Cost is the marketing spend on the Spend screen with that channel on it and its day
            inside the window, plus, for referral, the rewards that fell due in it. A channel nobody
            has typed a spend against reads a dash rather than nothing at all, and the four sources
            the plan budgets nothing for read free.
          </p>
          <p className={`mt-1 text-xs text-navy/60 ${MEASURE}`}>
            The planning figures were read on {formatDay(report.planningReadOn)} from{' '}
            {report.planningSource}.
          </p>
        </SplitLayout>
      )}
    </div>
  );
}

/** A day in the filter bar, shaped like the selects beside it rather than like a form field. */
function DayField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2.5">
      <label htmlFor={id} className="text-xs leading-4 tracking-wide text-navy/55 uppercase">
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 text-navy md:w-44"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Report B, at the top of the screen: the rules with a threshold.
 * ------------------------------------------------------------------ */

/**
 * THE SIX RULES, AS A NUMBER, A THRESHOLD AND A DAY, which is the whole of priority 7.
 *
 * Its own read, with its own refusal, for the reason the Today panels have theirs: this endpoint
 * counts over all of time and report A counts over the window on the filter bar, so one failing
 * must not blank the other. It is asked for once, with no `asOf`, because a threshold counted over
 * a period somebody chose is a threshold that moves when they press a select.
 *
 * A rule can carry two rows: Local Services counts leads under its own rule and is judged again
 * under the one that runs over every funded source, so the key is the rule AND the channel.
 */
function TheRulesWithAThreshold() {
  const [report, setReport] = useState<KillRulesReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    getKillRules()
      .then((given) => {
        if (live) setReport(given);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The rules did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  if (error) {
    return (
      <div className="mb-6">
        <ErrorNotice title="The rules did not load" message={error.message} onRetry={reload} />
      </div>
    );
  }
  if (report === null) {
    return (
      <div className="mb-6">
        <Busy what="the rules" />
      </div>
    );
  }

  const notes = ruleNotes(report.rules);

  return (
    <section className="mb-6">
      <h2 className="font-serif text-xl leading-7">The rules with a threshold</h2>
      <p className={`mt-1 mb-2 text-xs text-navy/60 ${MEASURE}`}>
        Counted from the first lead or the first dollar up to {formatDay(report.asOf)}, whatever
        period is chosen below. A rule near its threshold is drawn in coral, and it is the only
        thing on this screen that is.
      </p>

      <DataTable
        label="The rules with a threshold"
        groups={[{ key: 'rules', rows: report.rules }]}
        columns={ruleColumns(report.asOf)}
        rowKey={(row) => `${row.rule}-${row.channel}`}
        rowClassName={(row) =>
          ruleIsAGap(row)
            ? '[&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-coral [&>td:first-child]:pl-[13px]'
            : ''
        }
        card={(row) => <RuleCard row={row} asOf={report.asOf} />}
      />

      <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
        Cost per recurring household is judged over {formatDay(report.month.from)} to{' '}
        {formatDay(report.month.to)}, a whole month rather than a countdown: mid-month, one booking
        moves it by hundreds of dollars, so those rows carry no day and never appear on Today.
      </p>
      <p className={`mt-1 text-xs text-navy/60 ${MEASURE}`}>
        The thresholds were read on {formatDay(report.thresholdsReadOn)} from{' '}
        {report.thresholdsSource}, and that section hashes to {report.thresholdsHash}. The planning
        figures the twice-the-figure rows are judged against were read on{' '}
        {formatDay(report.planningReadOn)} from {report.planningSource}.
      </p>

      {notes.length === 0 ? null : (
        <dl className={`mt-3 space-y-2 ${MEASURE}`}>
          {notes.map((note) => (
            <div key={note.key}>
              <dt className="text-xs leading-4 tracking-wide text-navy/55 uppercase">
                {note.label}
              </dt>
              <dd className="text-xs text-navy/70">{note.note}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/**
 * Five columns, and the rule itself rides under the channel rather than taking a sixth: the words
 * of the rule and the channel it belongs to are one fact, and separating them puts a threshold on
 * the screen with nothing beside it saying what it counts.
 */
function ruleColumns(asOf: string): readonly DataColumn<KillRuleRow>[] {
  return [
    {
      key: 'rule',
      header: 'Rule',
      width: '26%',
      cell: (row) => (
        <>
          <span className="block font-serif text-base leading-tight">
            {ruleChannelWords(row.channel)}
          </span>
          <span className="block text-xs text-navy/70">{row.ruleText}</span>
        </>
      ),
    },
    {
      key: 'counters',
      header: 'Counted so far',
      width: '22%',
      cell: (row) => (
        <ul>
          {counterLines(row.rule, row.counters).map((line) => (
            <li key={line.label} className="text-xs leading-5">
              <span className="text-navy/60">{line.label} </span>
              <span className="font-medium">{line.value}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'threshold',
      header: 'Threshold',
      width: '14%',
      // The header word is the widest thing in the column, and at 768 it ran into the next one
      // (ops #56): the table keeps it and scrolls in its frame below that.
      minWidth: '96px',
      cellClassName: 'text-navy/75',
      cell: (row) => countWords(row.unit, row.threshold),
    },
    {
      key: 'left',
      header: 'What is left',
      width: '18%',
      minWidth: '88px',
      cell: (row) => <WhatIsLeft row={row} />,
    },
    {
      key: 'fires',
      header: 'Would fire',
      width: '20%',
      cell: (row) => <WouldFire row={row} asOf={asOf} />,
    },
  ];
}

/**
 * The countdown, and the only cell in this product that may be coral without something being
 * broken. A rule that can no longer fire says why in its own words under the dash, because a dash
 * with nothing beside it reads as a number nobody has worked out yet.
 */
function WhatIsLeft({ row }: { row: KillRuleRow }) {
  const words = remainingWords(row.rule, row.unit, row.remaining);
  const why = row.remaining === null ? whyNothingIsLeft(row.rule) : null;
  return (
    <>
      {ruleIsAGap(row) ? <GapPill>{words}</GapPill> : <span className="font-medium">{words}</span>}
      {why ? <span className="mt-0.5 block text-xs text-navy/60">{why}</span> : null}
    </>
  );
}

/** The day, and under it the one thing that decides whether to believe it. */
function WouldFire({ row, asOf }: { row: KillRuleRow; asOf: string }) {
  const note = firesOnNote(row.firesOn, asOf, row.projected);
  return (
    <>
      <span className="block">{firesOnWords(row.firesOn, asOf)}</span>
      {note ? <span className="block text-xs text-navy/60">{note}</span> : null}
    </>
  );
}

/** Below md, where every number is on the card because there is no table to put it in. */
function RuleCard({ row, asOf }: { row: KillRuleRow; asOf: string }) {
  const why = row.remaining === null ? whyNothingIsLeft(row.rule) : null;
  const note = firesOnNote(row.firesOn, asOf, row.projected);
  return (
    <Card className={ruleIsAGap(row) ? 'border-coral/50' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-lg leading-tight">{ruleChannelWords(row.channel)}</h3>
        {ruleIsAGap(row) ? <GapPill>Close to firing</GapPill> : null}
      </div>
      <p className="mt-1 text-sm text-navy/75">{row.ruleText}</p>
      <dl className="mt-1 grid grid-cols-2 gap-x-4">
        {counterLines(row.rule, row.counters).map((line) => (
          <Detail key={line.label} label={line.label} value={line.value} />
        ))}
        <Detail label="Threshold" value={countWords(row.unit, row.threshold)} />
        <Detail label="What is left" value={remainingWords(row.rule, row.unit, row.remaining)} />
        <Detail label="Would fire" value={firesOnWords(row.firesOn, asOf)} />
      </dl>
      {why ? <p className="mt-1 text-xs text-navy/65">{why}</p> : null}
      {note ? <p className="mt-1 text-xs text-navy/65">{note}</p> : null}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * The table, in two column sets. Below md it is not a table at all.
 * ------------------------------------------------------------------ */

type Pointing = (key: SortKey) => SortDirection;
type Turn = (key: SortKey) => void;
type FigureFor = (channel: Channel) => PlanningRange | null;
type Open = (channel: Channel) => void;

/** A count, with a zero drawn quietly: it is a real answer, and it is not the answer worth reading. */
function Count({ value }: { value: number }) {
  return value === 0 ? <span className="text-navy/45">0</span> : <span>{value}</span>;
}

function channelColumn(
  width: string,
  pointing: Pointing,
  turn: Turn,
  open: Open,
): DataColumn<ChannelReportRow> {
  return {
    key: 'channel',
    width,
    sort: pointing('channel'),
    header: (
      <SortableHeader
        label="Channel"
        direction={pointing('channel')}
        onSort={() => turn('channel')}
      />
    ),
    cell: (row) => (
      <button
        type="button"
        onClick={() => open(row.channel)}
        className="flex min-h-11 desk:min-h-9 w-full items-center text-left font-serif text-base"
      >
        <span>{channelWords(row.channel)}</span>
      </button>
    ),
  };
}

/**
 * THE WIDTHS ARE SHARES, NOT PIXELS, for the reason `screens/Money.tsx` sets out at length: a
 * fixed table takes the greater of its own width and the sum of its columns, so pixel widths that
 * add up to more than the room the rail leaves push the last columns off an edge with no way to
 * reach them. A share always resolves against the room there is.
 */
function everyColumn(
  pointing: Pointing,
  turn: Turn,
  figureFor: FigureFor,
  open: Open,
): readonly DataColumn<ChannelReportRow>[] {
  return [
    channelColumn('13%', pointing, turn, open),
    numberColumn('leads', 'Leads', '6%', pointing, turn, (row) => row.leads),
    plainColumn('booked', 'Booked', '8%', (row) => <Count value={row.booked} />),
    numberColumn('households', 'Households', '12%', pointing, turn, (row) => row.households),
    numberColumn('recurring', 'Recurring', '11%', pointing, turn, (row) => row.recurring),
    costColumn('6%', pointing, turn, figureFor),
    plainColumn('per-household', 'Per household', '12%', (row) => (
      <span>{costEachWords(row.costCents, row.households, figureFor(row.channel))}</span>
    )),
    perRecurringColumn('10%', figureFor),
    plainColumn('lead-to-booked', 'Lead to booked', '7%', (row) => (
      <span>{leadToBookedWords(row.leads, row.booked)}</span>
    )),
    plainColumn('reviews', 'Reviews', '9%', (row) => (
      <>
        <span className="block">{row.reviewsAsked} asked</span>
        <span className="block text-xs text-navy/65">{reviewsGivenWords(row.reviewsGiven)}</span>
      </>
    )),
    plainColumn('ended', 'Ended', '6%', (row) => <Count value={row.ended} />),
  ];
}

/**
 * What a tablet gets: the five that answer the question the screen exists for, which is which
 * channels have produced anything at all. The other six are one press away in the panel, and all
 * eleven are on the cards below md, so nothing is only readable on a desk.
 */
function theFiveThatFit(
  pointing: Pointing,
  turn: Turn,
  figureFor: FigureFor,
  open: Open,
): readonly DataColumn<ChannelReportRow>[] {
  return [
    // Households takes two points from the channel (ops #56): at 768 its header word and the sort
    // mark ran past a 22% cell, and the channel's words wrap where the header's cannot.
    channelColumn('22%', pointing, turn, open),
    numberColumn('leads', 'Leads', '13%', pointing, turn, (row) => row.leads),
    numberColumn('households', 'Households', '24%', pointing, turn, (row) => row.households),
    numberColumn('recurring', 'Recurring', '21%', pointing, turn, (row) => row.recurring),
    costColumn('20%', pointing, turn, figureFor),
  ];
}

function numberColumn(
  key: SortKey,
  label: string,
  width: string,
  pointing: Pointing,
  turn: Turn,
  read: (row: ChannelReportRow) => number,
): DataColumn<ChannelReportRow> {
  return {
    key,
    width,
    sort: pointing(key),
    header: <SortableHeader label={label} direction={pointing(key)} onSort={() => turn(key)} />,
    cell: (row) => <Count value={read(row)} />,
  };
}

/**
 * A column nothing sorts by. Its cells are drawn at the same weight as the sorted ones, because
 * every number in this table is a report number of equal standing and a quieter one would read as
 * a lesser fact rather than as a column with no chevron on it.
 */
function plainColumn(
  key: string,
  header: string,
  width: string,
  cell: (row: ChannelReportRow) => ReactNode,
): DataColumn<ChannelReportRow> {
  return { key, header, width, cell };
}

function costColumn(
  width: string,
  pointing: Pointing,
  turn: Turn,
  figureFor: FigureFor,
): DataColumn<ChannelReportRow> {
  return {
    key: 'cost',
    width,
    sort: pointing('cost'),
    header: (
      <SortableHeader label="Cost" direction={pointing('cost')} onSort={() => turn('cost')} />
    ),
    cell: (row) => <span>{costWords(row.costCents, figureFor(row.channel))}</span>,
  };
}

function perRecurringColumn(width: string, figureFor: FigureFor): DataColumn<ChannelReportRow> {
  return {
    key: 'per-recurring',
    width,
    header: 'Per recurring',
    cell: (row) => {
      const figure = figureFor(row.channel);
      return (
        <PerRecurring
          words={costEachWords(row.costCents, row.recurring, figure)}
          figure={figure}
          small
        />
      );
    },
  };
}

/** The real figure, and under it the two the plan wrote down. Never a comparison of the three. */
function PerRecurring({
  words,
  figure,
  small,
}: {
  words: string;
  /** Either shape of planning entry: the channel report carries a fuller one than the list does. */
  figure: PlanningCents | null;
  small?: boolean;
}) {
  const range = planningRangeWords(figure);
  const twice = twiceTheTopWords(figure);
  return (
    <>
      <span className="block">{words}</span>
      {range === null ? null : (
        <span className={`block text-navy/65 ${small ? 'text-xs' : 'text-sm'}`}>
          Planned {range}
        </span>
      )}
      {twice === null ? null : (
        <span className={`block text-navy/65 ${small ? 'text-xs' : 'text-sm'}`}>
          Twice the top is {twice}
        </span>
      )}
    </>
  );
}

/** Below md, where every number is on the card because there is no table to put it in. */
function ChannelCard({
  row,
  figure,
  opened,
  onOpen,
}: {
  row: ChannelReportRow;
  figure: PlanningRange | null;
  opened: boolean;
  onOpen: () => void;
}) {
  return (
    <button type="button" onClick={onOpen} className="block w-full text-left">
      <Card className={opened ? 'border-gold' : ''}>
        <h2 className="font-serif text-lg leading-tight">{channelWords(row.channel)}</h2>
        <dl className="mt-1 grid grid-cols-2 gap-x-4">
          <Detail label="Leads" value={row.leads} />
          <Detail label="Booked" value={row.booked} />
          <Detail label="Households" value={row.households} />
          <Detail label="Recurring" value={row.recurring} />
          <Detail label="Cost" value={costWords(row.costCents, figure)} />
          <Detail
            label="Per household"
            value={costEachWords(row.costCents, row.households, figure)}
          />
          <Detail label="Lead to booked" value={leadToBookedWords(row.leads, row.booked)} />
          <Detail label="Ended" value={row.ended} />
          <Detail label="Reviews" value={reviewsWords(row.reviewsAsked, row.reviewsGiven)} />
          <Detail
            label="Per recurring"
            value={
              <PerRecurring
                words={costEachWords(row.costCents, row.recurring, figure)}
                figure={figure}
              />
            }
          />
        </dl>
      </Card>
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * One channel, by the words somebody typed under it.
 * ------------------------------------------------------------------ */

function ChannelPanel({
  channel,
  row,
  from,
  to,
  onClose,
}: {
  channel: Channel;
  /** The channel's own row of the report, for its money (ops #86). */
  row?: ChannelReportRow;
  from: string;
  to: string;
  onClose: () => void;
}) {
  const [report, setReport] = useState<ChannelReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    setReport(null);
    getChannelReport(channel, from, to)
      .then((given) => {
        if (live) setReport(given);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'That channel did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [channel, from, to]);

  return (
    <SidePanel title={channelWords(channel)} onClose={onClose}>
      {error ? (
        <ErrorNotice title="That channel did not load" message={error.message} />
      ) : report === null ? (
        <Busy what="the channel" />
      ) : (
        <ChannelRollUp report={report} row={row} />
      )}
    </SidePanel>
  );
}

/**
 * THE CHANNEL'S MONEY (ops #86, `docs/plan-coach-review.md` section 6): what its households paid in the
 * window, and on an owner's answer alone the cleaner pay of its done cleans and what is left after it.
 * "After cleaner pay", never profit, and the cleans no rate covered said in words. Drawn only from
 * what the server sent: a manager's answer carries the revenue and nothing more.
 */
function ChannelMoney({ row }: { row: ChannelReportRow }) {
  if (row.revenueCents === undefined) return null;
  const owners = row.cleanerPayCents !== undefined;
  return (
    <>
      <PanelRule />
      <h3 className="font-serif text-lg leading-7">Money</h3>
      <dl className="grid grid-cols-2 gap-x-4">
        <Detail label="Paid in the window" value={money(row.revenueCents)} />
        {owners ? (
          <>
            <Detail label="Cleaner pay" value={money(row.cleanerPayCents ?? 0)} />
            <Detail label="After cleaner pay" value={money(row.afterPayCents ?? 0)} />
          </>
        ) : null}
      </dl>
      {owners && (row.unpricedCleans ?? 0) > 0 ? (
        <p className="text-sm text-coral">
          {row.unpricedCleans === 1
            ? '1 clean with no rate on file, not counted as nothing.'
            : `${row.unpricedCleans} cleans with no rate on file, not counted as nothing.`}
        </p>
      ) : null}
      <p className="text-xs text-navy/65">
        Every household of the channel, each figure by its own days in the window, so neither is
        divided by the households above.
        {owners ? ' Not profit: supplies, the drive, insurance and card fees are not in it.' : ''}
      </p>
    </>
  );
}

function ChannelRollUp({ report, row }: { report: ChannelReport; row?: ChannelReportRow }) {
  const total = sumDetails(report.details);
  const figure = report.planning;

  return (
    <>
      <p className="text-sm text-navy/75">
        {formatDay(report.from)} to {formatDay(report.to)}
      </p>

      {/*
       * The channel's own totals, added up from the roll-up rather than asked for again, so this
       * panel is the whole of the eleven columns at every width. A tablet is shown five of them in
       * the table and reads the other six here.
       */}
      <dl className="grid grid-cols-2 gap-x-4">
        <Detail label="Leads" value={total.leads} />
        <Detail label="Booked" value={total.booked} />
        <Detail label="Households" value={total.households} />
        <Detail label="Recurring" value={total.recurring} />
        <Detail label="Cost" value={costWords(report.costCents, figure)} />
        <Detail
          label="Per household"
          value={costEachWords(report.costCents, total.households, figure)}
        />
        <Detail label="Lead to booked" value={leadToBookedWords(total.leads, total.booked)} />
        <Detail label="Ended" value={total.ended} />
        <Detail label="Reviews" value={reviewsWords(total.reviewsAsked, total.reviewsGiven)} />
        <Detail
          label="Per recurring"
          value={
            <PerRecurring
              words={costEachWords(report.costCents, total.recurring, figure)}
              figure={figure}
            />
          }
        />
      </dl>

      {row ? <ChannelMoney row={row} /> : null}

      <PanelRule />
      <h3 className="font-serif text-lg leading-7">The words typed under it</h3>
      {report.details.length === 0 ? (
        <p className="text-sm text-navy/70">
          Nothing was recorded on this channel between those two days.
        </p>
      ) : (
        <ul className="space-y-2">
          {report.details.map((row) => (
            <li key={row.detail ?? ''} className="rounded-lg border border-navy/12 bg-cream/50 p-3">
              <p className="font-medium">{detailWords(row.detail)}</p>
              <dl className="grid grid-cols-2 gap-x-4">
                <Detail label="Leads" value={row.leads} />
                <Detail label="Booked" value={row.booked} />
                <Detail label="Households" value={row.households} />
                <Detail label="Recurring" value={row.recurring} />
                <Detail label="Lead to booked" value={leadToBookedWords(row.leads, row.booked)} />
                <Detail label="Ended" value={row.ended} />
                <Detail label="Reviews" value={reviewsWords(row.reviewsAsked, row.reviewsGiven)} />
              </dl>
            </li>
          ))}
        </ul>
      )}

      {/*
       * WHAT HELD ITS OPEN QUOTES BACK (ops #86): the enquiries of the window still quoted, by the
       * word the office typed, so "price" against "no answer" is a figure per source.
       */}
      {(report.heldBack ?? []).length === 0 ? null : (
        <>
          <PanelRule />
          <h3 className="font-serif text-lg leading-7">What held its open quotes back</h3>
          <ul className="space-y-1 text-sm">
            {(report.heldBack ?? []).map((row) => (
              <li key={row.reason ?? ''} className="flex justify-between gap-3">
                <span>{row.reason ? HELD_BACK_LABELS[row.reason] : 'Not said yet'}</span>
                <span className="tabular-nums">{row.quotes}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {report.introducers.length === 0 ? null : (
        <>
          <PanelRule />
          <h3 className="font-serif text-lg leading-7">Who introduced whom</h3>
          <ul className="space-y-1.5 text-sm">
            {report.introducers.map((row, index) => (
              <li key={`${index}-${row.clientName}`}>
                <span>
                  {row.referrerName ?? 'Somebody no longer in the book'} introduced {row.clientName}
                </span>
                <span className="block text-xs text-navy/65">{formatDay(row.cohortDate)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
